import React, { useRef, useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import * as THREE from 'three'
import { useSettings } from '../../contexts/SettingsContext'

const LyoAvatar = ({ mood = 50, isListening = false, isSpeaking = false, emotion = 'neutral' }) => {
  const containerRef = useRef()
  const rendererRef = useRef()
  const sceneRef = useRef()
  const cameraRef = useRef()
  const avatarRef = useRef()
  const { settings } = useSettings()
  const [currentEmotion, setCurrentEmotion] = useState(emotion)
  
  // Calculate color based on mood and emotion
  const getMoodColor = () => {
    if (isListening) return 0x00ff88 // Green when listening
    if (isSpeaking) return 0xff6b35 // Orange when speaking
    
    switch (currentEmotion) {
      case 'happy': return 0x10b981
      case 'sad': return 0x6b7280
      case 'excited': return 0xf59e0b
      case 'calm': return 0x00e5ff
      case 'thinking': return 0x8b5cf6
      default:
        if (mood < 25) return 0xff4444
        if (mood < 50) return 0xf59e0b
        if (mood < 75) return 0x00e5ff
        return 0x10b981
    }
  }
  
  // Get eye expression based on emotion
  const getEyeExpression = () => {
    switch (currentEmotion) {
      case 'happy': return { scaleY: 0.6, offsetY: 0.1 }
      case 'sad': return { scaleY: 1.2, offsetY: -0.1 }
      case 'excited': return { scaleY: 1.5, offsetY: 0.2 }
      case 'thinking': return { scaleY: 0.8, offsetY: 0 }
      default: return { scaleY: 1, offsetY: 0 }
    }
  }
  
  useEffect(() => {
    setCurrentEmotion(emotion)
  }, [emotion])
  
  useEffect(() => {
    if (!containerRef.current) return
    
    // Initialize Three.js scene
    const width = containerRef.current.clientWidth
    const height = containerRef.current.clientHeight
    
    // Scene setup
    const scene = new THREE.Scene()
    sceneRef.current = scene
    
    // Camera setup
    const camera = new THREE.PerspectiveCamera(50, width / height, 0.1, 1000)
    camera.position.z = 5
    cameraRef.current = camera
    
    // Renderer setup
    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true })
    renderer.setSize(width, height)
    renderer.setPixelRatio(window.devicePixelRatio)
    containerRef.current.appendChild(renderer.domElement)
    rendererRef.current = renderer
    
    // Lights
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.6)
    scene.add(ambientLight)
    
    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8)
    directionalLight.position.set(0, 10, 5)
    scene.add(directionalLight)
    
    // Create avatar based on style
    const createAvatar = () => {
      // Remove existing avatar if any
      if (avatarRef.current) {
        scene.remove(avatarRef.current)
      }
      
      const avatarGroup = new THREE.Group()
      
      if (settings.avatarStyle === 'holographic') {
        // Main head sphere with wireframe
        const headGeometry = new THREE.SphereGeometry(1.5, 32, 32)
        const wireframeMaterial = new THREE.MeshBasicMaterial({
          color: getMoodColor(),
          wireframe: true,
          transparent: true,
          opacity: 0.8
        })
        const head = new THREE.Mesh(headGeometry, wireframeMaterial)
        avatarGroup.add(head)
        
        // Inner glow sphere
        const innerGlowGeometry = new THREE.SphereGeometry(1.3, 16, 16)
        const innerGlowMaterial = new THREE.MeshBasicMaterial({
          color: getMoodColor(),
          transparent: true,
          opacity: 0.3
        })
        const innerGlow = new THREE.Mesh(innerGlowGeometry, innerGlowMaterial)
        avatarGroup.add(innerGlow)
        
        // Animated eyes
        const eyeExpression = getEyeExpression()
        const eyeGeometry = new THREE.SphereGeometry(0.15, 16, 16)
        const eyeMaterial = new THREE.MeshBasicMaterial({
          color: getMoodColor(),
          transparent: true,
          opacity: 0.9
        })
        
        const leftEye = new THREE.Mesh(eyeGeometry, eyeMaterial)
        leftEye.position.set(-0.5, 0.2 + eyeExpression.offsetY, 1.2)
        leftEye.scale.y = eyeExpression.scaleY
        avatarGroup.add(leftEye)
        
        const rightEye = new THREE.Mesh(eyeGeometry, eyeMaterial)
        rightEye.position.set(0.5, 0.2 + eyeExpression.offsetY, 1.2)
        rightEye.scale.y = eyeExpression.scaleY
        avatarGroup.add(rightEye)
        
        // Mouth indicator for speaking
        if (isSpeaking) {
          const mouthGeometry = new THREE.RingGeometry(0.1, 0.2, 8)
          const mouthMaterial = new THREE.MeshBasicMaterial({
            color: getMoodColor(),
            transparent: true,
            opacity: 0.7
          })
          const mouth = new THREE.Mesh(mouthGeometry, mouthMaterial)
          mouth.position.set(0, -0.3, 1.2)
          avatarGroup.add(mouth)
        }
        
        // Listening indicator
        if (isListening) {
          const ringGeometry = new THREE.RingGeometry(2, 2.2, 32)
          const ringMaterial = new THREE.MeshBasicMaterial({
            color: 0x00ff88,
            transparent: true,
            opacity: 0.5
          })
          const listeningRing = new THREE.Mesh(ringGeometry, ringMaterial)
          avatarGroup.add(listeningRing)
        }
        
      } else if (settings.avatarStyle === 'cartoon') {
        // Enhanced cartoon style with expressions
        const bodyGeometry = new THREE.SphereGeometry(1.2, 32, 32)
        const bodyMaterial = new THREE.MeshToonMaterial({
          color: getMoodColor(),
          transparent: true,
          opacity: 0.9
        })
        const body = new THREE.Mesh(bodyGeometry, bodyMaterial)
        avatarGroup.add(body)
        
        // Animated eyes based on emotion - using CircleGeometry with scaling for elliptical effect
        const eyeExpression = getEyeExpression()
        const eyeGeometry = new THREE.CircleGeometry(0.2, 16)
        const eyeMaterial = new THREE.MeshBasicMaterial({
          color: 0xffffff
        })
        
        const leftEye = new THREE.Mesh(eyeGeometry, eyeMaterial)
        leftEye.position.set(-0.4, 0.2 + eyeExpression.offsetY, 1.1)
        leftEye.scale.set(1, eyeExpression.scaleY, 1) // Apply Y scaling for elliptical effect
        avatarGroup.add(leftEye)
        
        const rightEye = new THREE.Mesh(eyeGeometry, eyeMaterial)
        rightEye.position.set(0.4, 0.2 + eyeExpression.offsetY, 1.1)
        rightEye.scale.set(1, eyeExpression.scaleY, 1) // Apply Y scaling for elliptical effect
        avatarGroup.add(rightEye)
        
        // Pupils
        const pupilGeometry = new THREE.CircleGeometry(0.08, 16)
        const pupilMaterial = new THREE.MeshBasicMaterial({ color: 0x000000 })
        
        const leftPupil = new THREE.Mesh(pupilGeometry, pupilMaterial)
        leftPupil.position.set(-0.4, 0.2 + eyeExpression.offsetY, 1.12)
        avatarGroup.add(leftPupil)
        
        const rightPupil = new THREE.Mesh(pupilGeometry, pupilMaterial)
        rightPupil.position.set(0.4, 0.2 + eyeExpression.offsetY, 1.12)
        avatarGroup.add(rightPupil)
        
        // Mouth based on emotion and speaking state
        let mouthGeometry
        if (isSpeaking) {
          mouthGeometry = new THREE.RingGeometry(0.1, 0.15, 16)
        } else {
          switch (currentEmotion) {
            case 'happy':
              mouthGeometry = new THREE.RingGeometry(0.05, 0.1, 16, 1, 0, Math.PI)
              break
            case 'sad':
              mouthGeometry = new THREE.RingGeometry(0.05, 0.1, 16, 1, Math.PI, Math.PI)
              break
            default:
              mouthGeometry = new THREE.CircleGeometry(0.05, 16)
          }
        }
        
        const mouthMaterial = new THREE.MeshBasicMaterial({
          color: isSpeaking ? 0xff6b35 : 0x000000
        })
        const mouth = new THREE.Mesh(mouthGeometry, mouthMaterial)
        mouth.position.set(0, -0.2, 1.12)
        avatarGroup.add(mouth)
      }
      
      scene.add(avatarGroup)
      avatarRef.current = avatarGroup
    }
    
    createAvatar()
    
    // Animation loop
    const animate = () => {
      const animationId = requestAnimationFrame(animate)
      
      if (avatarRef.current) {
        // Base rotation
        avatarRef.current.rotation.y += 0.005
        
        // Enhanced floating animation
        const time = Date.now() * 0.001
        avatarRef.current.position.y = Math.sin(time) * 0.1
        
        // Breathing effect
        const breathScale = 1 + Math.sin(time * 2) * 0.02
        avatarRef.current.scale.set(breathScale, breathScale, breathScale)
        
        // Speaking animation
        if (isSpeaking) {
          avatarRef.current.rotation.z = Math.sin(time * 8) * 0.05
        }
        
        // Listening pulse
        if (isListening) {
          const pulseScale = 1 + Math.sin(time * 4) * 0.1
          avatarRef.current.scale.set(pulseScale, pulseScale, pulseScale)
        }
      }
      
      renderer.render(scene, camera)
      
      return () => {
        cancelAnimationFrame(animationId)
      }
    }
    
    animate()
    
    // Handle window resize
    const handleResize = () => {
      if (!containerRef.current) return
      
      const width = containerRef.current.clientWidth
      const height = containerRef.current.clientHeight
      
      camera.aspect = width / height
      camera.updateProjectionMatrix()
      renderer.setSize(width, height)
    }
    
    window.addEventListener('resize', handleResize)
    
    return () => {
      window.removeEventListener('resize', handleResize)
      
      if (rendererRef.current && containerRef.current) {
        containerRef.current.removeChild(rendererRef.current.domElement)
      }
    }
  }, [settings.avatarStyle, currentEmotion, isListening, isSpeaking])
  
  // Update colors based on mood and state changes
  useEffect(() => {
    if (!avatarRef.current) return
    
    const color = getMoodColor()
    
    avatarRef.current.children.forEach(child => {
      if (child.material && child.material.color) {
        child.material.color.setHex(color)
      }
    })
  }, [mood, isListening, isSpeaking, currentEmotion])
  
  return (
    <motion.div 
      ref={containerRef}
      className="w-full h-full relative"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1 }}
    >
      {/* Voice visualization overlay */}
      {(isListening || isSpeaking) && (
        <motion.div
          className="absolute inset-0 pointer-events-none"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className={`absolute inset-0 rounded-full ${
            isListening ? 'bg-green-400/20' : 'bg-orange-400/20'
          } animate-pulse`} />
          <div className={`absolute inset-4 rounded-full ${
            isListening ? 'bg-green-400/10' : 'bg-orange-400/10'
          } animate-ping`} />
        </motion.div>
      )}
    </motion.div>
  )
}

export default LyoAvatar